import {Injectable} from '@angular/core';
import {Order} from "../../model/clearing/order";
import {Trade} from "../../model/clearing/trade";
import {
  OrderAmendmentTableComponent
} from "../../crystal-clear/data-amendment/order-amendment/table/order-amendment-table.component";
import {TableEditColumnConfig} from "../../model/api/table-edit-column-config";
import {FormFieldConfig} from "../../model/api/form-field-config";
import {FieldsService} from "../common/fields-service";
import {AppConfigService} from "../app-config.service";
import {RolesService} from "../auth/roles.service";
import {OrderAmendmentDataLoadingService} from "./order-amendment-data-loading.service";
import TYPE = FormFieldConfig.TYPE;
import Side = Trade.Side;
import {AccountDefinition} from "../../model/customer/account-definition";
import {TranslateService} from "@ngx-translate/core";

@Injectable({
  providedIn: 'root'
})
export class OrderAmendmentTableConfigService {


  constructor(private appConfigService: AppConfigService,
              private fieldsService: FieldsService,
              private rolesService: RolesService,
              private dataLoadingService: OrderAmendmentDataLoadingService,
              private  translateService: TranslateService,) {
  }

  public loadConfig(component: OrderAmendmentTableComponent): TableEditColumnConfig[] {
    return [
      {
        field: 'exchangeCode',
        formFieldConfig: {
          field: 'exchangeCode',
          column: 2,
          options: () => this.dataLoadingService.exchanges,
          icon: (id: string) => {
            if (this.dataLoadingService.exchanges) {
              const exchField = this.dataLoadingService.exchanges.find(exchange => exchange.id === id);
              if (exchField && exchField.value)
                return exchField.value.country ? 'flag-icon flag-icon-' + exchField.value.country.toLowerCase() : '';
            }
            return 'fas fa-globe-europe';
          },
          type: TYPE.DROPDOWN,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.rolesService.isAdmin(),
        icon: (record: AccountDefinition) => {
          if (this.dataLoadingService.exchanges) {
            const exchField = this.dataLoadingService.exchanges.find(exchange => exchange.value.code === record.exchangeCode);
            if (exchField && exchField.value)
              return exchField.value.country ? 'flag-icon flag-icon-' + exchField.value.country.toLowerCase() : '';
          }
          return 'fas fa-globe-europe';
        },
        mapId: record => {
          if (!record.exchangeCode || record.exchangeCode.length == 0) {
            return this.translateService.instant('page.account.form.defaultExchangeAll');
          } else {
            return record.exchangeCode
          }
        }
      },
      {
        field: 'side',
        formFieldConfig: {
          field: 'side',
          column: 2,
          options: () => this.dataLoadingService.sides,
          type: TYPE.DROPDOWN,
        },
        class: record => this.getRowClass(record),
        editable: (record) =>  this.rolesService.isAdmin(),
      },
      {
        field: 'volume',
        width: 90,
        formFieldConfig: {
          field: 'volume',
          column: 2,
          type: TYPE.INTEGER,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'maturity',
        formFieldConfig: {
          field: 'maturity',
          column: 2,
          type: TYPE.TEXT,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'contractCode',
        formFieldConfig: {
          field: 'contractCode',
          column: 2,
          type: TYPE.TEXT,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'price',
        width: 90,
        formFieldConfig: {
          field: 'price',
          column: 2,
          type: TYPE.DECIMAL,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'avgPrice',
        width: 90,
        formFieldConfig: {
          field: 'avgPrice',
          column: 2,
          type: TYPE.DECIMAL,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'reference',
        formFieldConfig: {
          field: 'reference',
          column: 2,
          type: TYPE.TEXT,
          input: (event, formRecord, inputField) => {
            var start = inputField.selectionStart;
            var end = inputField.selectionEnd;
            inputField.value = inputField.value.toUpperCase();
            formRecord.reference = inputField.value;
            inputField.setSelectionRange(start, end);
          },
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'avgPriceGroupId',
        formFieldConfig: {
          field: 'avgPriceGroupId',
          column: 2,
          type: TYPE.TEXT,
          input: (event, formRecord, inputField) => {
            var start = inputField.selectionStart;
            var end = inputField.selectionEnd;
            inputField.value = inputField.value.toUpperCase();
            formRecord.avgPriceGroupId = inputField.value;
            inputField.setSelectionRange(start, end);
          },
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'customerName',
        width: 180,
        formFieldConfig: {
          field: 'customerName', column: 2,
          type: TYPE.AUTOCOMPLETE,
          options: (record, query) => {
            this.dataLoadingService.filterCustomers(record);
            return this.fieldsService.getDistinctOpts(this.dataLoadingService.displayedCustomers)
          },
          changed: (event, record) => {
            queueMicrotask(() => {
              this.dataLoadingService.forceCustomerValues(record, event.text, event.id);
              component.reloadTableValueMap();
            });
          }
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record)
      },
      {
        field: 'accountName',
        width: 180,
        formFieldConfig: {
          field: 'accountName',
          column: 2,
          type: TYPE.AUTOCOMPLETE,
          options: (record, query) => {
            return this.fieldsService.getDistinctOpts(this.dataLoadingService.filterAccounts(record, query));
          },
          changed: (event, record) => {
            queueMicrotask(() => {
              this.dataLoadingService.forceAccountValues(record, event.text);
              component.reloadTableValueMap();
            });

          }
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record)
      },
      {
        field: 'accountType',
        formFieldConfig: {
          field: 'accountType',
          column: 2,
          type: TYPE.AUTOCOMPLETE,
          options: (record) => {
            this.dataLoadingService.filterAccountTypes(record)
            return this.fieldsService.getDistinctOpts(this.dataLoadingService.displayedAccountTypes)
          },
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record)
      },
      {
        field: 'giveUpBrokerCode',
        width: 180,
        formFieldConfig: {
          field: 'giveUpBrokerCode',
          column: 2,
          type: TYPE.TEXT,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'giveUpMessage',
        width: 180,
        formFieldConfig: {
          field: 'giveUpMessage',
          column: 2,
          type: TYPE.TEXT,
          input: (event, formRecord, inputField) => {
            var start = inputField.selectionStart;
            var end = inputField.selectionEnd;
            inputField.value = inputField.value.toUpperCase();
            formRecord.giveUpMessage = inputField.value;
            inputField.setSelectionRange(start, end);
          },
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record)
      },
      {
        field: 'accountReference1',
        width: 180,
        formFieldConfig: {
          field: 'accountReference1',
          column: 2,
          type: TYPE.TEXT,
          input: (event, formRecord, inputField) => {
            var start = inputField.selectionStart;
            var end = inputField.selectionEnd;
            inputField.value = inputField.value.toUpperCase();
            formRecord.accountReference1 = inputField.value;
            inputField.setSelectionRange(start, end);
          },
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record)
      },
      {
        field: 'clearingBroker',
        formFieldConfig: {
          field: 'clearingBroker',
          column: 2,
          type: TYPE.DROPDOWN,
          filter: true,
          options: () => {
            return this.fieldsService.getDistinctOpts(this.dataLoadingService.moneyOwners)
          },
          changed: (event, record) => {
            record.moneyOwner = event.text;
          }
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'brokerageFee',
        formFieldConfig: {
          field: 'brokerageFee',
          column: 2,
          type: TYPE.DECIMAL,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'brokerageCurrency',
        formFieldConfig: {
          field: 'brokerageCurrency',
          column: 2,
          type: TYPE.TEXT,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'brokerageRateAmount',
        formFieldConfig: {
          field: 'brokerageRateAmount',
          column: 2,
          type: TYPE.DECIMAL,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'submittingTrader',
        width: 150,
        formFieldConfig: {
          field: 'submittingTrader',
          column: 2,
          type: TYPE.AUTOCOMPLETE,
          options: (record) => this.fieldsService.getDistinctOpts(this.dataLoadingService.execTraders),
          changed: (event, record) => {
            queueMicrotask(() => this.dataLoadingService.setBrokerageRate(record));
          }
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'trader',
        width: 150,
        formFieldConfig: {
          field: 'trader',
          column: 2,
          type: TYPE.AUTOCOMPLETE,
          filter: true,
          options: (record) => this.fieldsService.getDistinctOpts(this.dataLoadingService.filterTraders(record)),
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'tradeType',
        formFieldConfig: {
          field: 'tradeType',
          column: 2,
          type: TYPE.TEXT,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'strategy',
        formFieldConfig: {
          field: 'strategy',
          column: 2,
          options: () => this.dataLoadingService.strategies,
          type: TYPE.DROPDOWN,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'contractUnit',
        formFieldConfig: {
          field: 'contractUnit',
          column: 2,
          type: TYPE.TEXT,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'callPut',
        formFieldConfig: {
          field: 'callPut',
          column: 2,
          options: () => this.dataLoadingService.callPuts,
          type: TYPE.DROPDOWN,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'strike',
        formFieldConfig: {
          field: 'strike',
          column: 2,
          type: TYPE.DECIMAL,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'overrideAvgPrice',
        formFieldConfig: {
          field: 'overrideAvgPrice',
          column: 2,
          type: TYPE.DECIMAL,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'avgPriceResidual',
        formFieldConfig: {
          field: 'avgPriceResidual',
          column: 2,
          type: TYPE.DECIMAL,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'avgRndPrice',
        formFieldConfig: {
          field: 'avgRndPrice',
          column: 2,
          type: TYPE.DECIMAL,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'state',
        formFieldConfig: {
          field: 'state',
          column: 2,
          options: () => this.dataLoadingService.orderStates,
          type: TYPE.DROPDOWN,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'clearingDate',
        format: 'dd/MM/yyyy',
        type: 'DATE',
        formFieldConfig: {
          field: 'clearingDate',
          column: 2,
          type: TYPE.DATE,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'businessDate',
        format: 'dd/MM/yyyy',
        type: 'DATE',
        formFieldConfig: {
          field: 'businessDate',
          column: 2,
          type: TYPE.DATE,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      },
      {
        field: 'executionDate',
        width: 130,
        format: 'dd/MM/yyyy',
        type: 'DATE',
        formFieldConfig: {
          field: 'executionDate',
          column: 2,
          type: TYPE.DATETIME,
        },
        class: record => this.getRowClass(record),
        editable: (record) => this.isEditable(record),
      }];
  }

  private getRowClass(record: Order) {
    if (record.manualInvoiceRemoved)
      return "order-removed";
    else if (record.settlementId != 0)
      return "order-paid";
    else if (Side.BUY === record.side)
      return "side-buy";
    else if (Side.SELL === record.side)
      return "side-sell";
  }

  private isEditable(record: Order): boolean {
    return record.exchangeCode && record.exchangeCode.endsWith(this.appConfigService.flexGwSuffix) || this.rolesService.isAdmin();
  }
}
